/**
 * Timezone utilities for UK business date/time handling.
 *
 * All Book4 businesses currently operate in Europe/London.
 * When multi-timezone support is added, BUSINESS_TIMEZONE should be
 * read from the Business model instead of this constant.
 */

export const BUSINESS_TIMEZONE = "Europe/London";

/**
 * Get the UTC offset in minutes for a timezone at a specific instant.
 * Positive means ahead of UTC (e.g. BST = +60, GMT = 0).
 */
export function getTimezoneOffsetMinutes(
  date: Date,
  tz: string = BUSINESS_TIMEZONE,
): number {
  const utcStr = date.toLocaleString("en-GB", {
    timeZone: "UTC",
    hour12: false,
  });
  const tzStr = date.toLocaleString("en-GB", {
    timeZone: tz,
    hour12: false,
  });

  function parseLocale(s: string): number {
    // en-GB format: "DD/MM/YYYY, HH:MM:SS"
    const [datePart, timePart] = s.split(", ");
    const [day, month, year] = datePart.split("/").map(Number);
    const timeParts = timePart.split(":").map(Number);
    const h = timeParts[0] === 24 ? 0 : timeParts[0];
    return Date.UTC(year, month - 1, day, h, timeParts[1], timeParts[2]);
  }

  return (parseLocale(tzStr) - parseLocale(utcStr)) / (60 * 1000);
}

/**
 * Convert a wall-clock date + time in a timezone to a UTC Date.
 *
 * @param dateStr "YYYY-MM-DD"
 * @param timeStr "HH:MM"
 * @param tz      IANA timezone identifier (default Europe/London)
 */
export function wallTimeToUTC(
  dateStr: string,
  timeStr: string,
  tz: string = BUSINESS_TIMEZONE,
): Date {
  const utcApprox = new Date(`${dateStr}T${timeStr}:00Z`);
  const offsetMinutes = getTimezoneOffsetMinutes(utcApprox, tz);
  const result = new Date(utcApprox.getTime() - offsetMinutes * 60 * 1000);

  // Verify: the offset at the result instant might differ from our approximation
  // (DST transitions). Re-check and correct if needed.
  const resultOffset = getTimezoneOffsetMinutes(result, tz);
  if (resultOffset !== offsetMinutes) {
    return new Date(utcApprox.getTime() - resultOffset * 60 * 1000);
  }
  return result;
}

/**
 * Convert a UTC Date to an "HH:MM" wall-clock time string in a timezone.
 */
export function utcToWallTime(
  utcDate: Date,
  tz: string = BUSINESS_TIMEZONE,
): string {
  return utcDate.toLocaleTimeString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Get start-of-day and end-of-day for a calendar date in a timezone,
 * returned as UTC Date objects.
 */
export function getDayBoundsUTC(
  dateStr: string,
  tz: string = BUSINESS_TIMEZONE,
): { startOfDay: Date; endOfDay: Date } {
  const start = wallTimeToUTC(dateStr, "00:00", tz);
  const endApprox = wallTimeToUTC(dateStr, "23:59", tz);
  const end = new Date(endApprox.getTime() + 59 * 1000 + 999);
  return { startOfDay: start, endOfDay: end };
}

/**
 * Get the day-of-week (0 = Sunday, 6 = Saturday) for a calendar date
 * in a timezone. Uses the wall-clock date, not UTC.
 */
export function getDayOfWeekInTimezone(
  dateStr: string,
  tz: string = BUSINESS_TIMEZONE,
): number {
  const noon = new Date(`${dateStr}T12:00:00Z`);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    weekday: "short",
  }).formatToParts(noon);
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[weekday] ?? noon.getUTCDay();
}

/**
 * Intl.DateTimeFormat options preset for Europe/London timezone.
 * Spread into toLocaleDateString / toLocaleTimeString option objects.
 */
export const TZ_DISPLAY = { timeZone: BUSINESS_TIMEZONE } as const;
